import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function formatPercent(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `${num.toFixed(2)}%`;
}

function formatPrice(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function computeReturnPct(startPrice, endPrice) {
  const start = Number(startPrice);
  const end = Number(endPrice);

  if (!Number.isFinite(start) || !Number.isFinite(end) || start === 0) {
    return null;
  }

  return ((end - start) / start) * 100;
}

function getRealReturn(confidence, returnPct) {
  const c = Number(confidence);
  const r = Number(returnPct);

  if (!Number.isFinite(c) || !Number.isFinite(r)) return null;
  return c * r;
}

const pageStyle = {
  maxWidth: 1400,
  margin: "0 auto",
  padding: 24,
};

const cardGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 16,
  marginBottom: 28,
};

const cardStyle = {
  border: "1px solid #ddd",
  borderRadius: 14,
  padding: 18,
  background: "#fff",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
};

const thStyle = {
  textAlign: "left",
  padding: "12px 10px",
  borderBottom: "1px solid #e5e5e5",
  background: "#fafafa",
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  verticalAlign: "top",
};

const tdStyle = {
  padding: "12px 10px",
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "top",
  fontSize: 14,
};

const expandCellStyle = {
  ...tdStyle,
  width: 44,
  textAlign: "center",
  paddingLeft: 8,
  paddingRight: 8,
};

const expandButtonStyle = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: 16,
  lineHeight: 1,
  padding: 4,
};

const detailRowStyle = {
  background: "#fafafa",
};

const detailContentStyle = {
  padding: "14px 16px",
  whiteSpace: "pre-wrap",
  lineHeight: 1.5,
};

function StatCard({ title, value, subtext }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1 }}>{value}</div>
      {subtext ? (
        <div style={{ marginTop: 8, fontSize: 12, color: "#777" }}>{subtext}</div>
      ) : null}
    </div>
  );
}

export default function PortfolioPage() {
  const { username } = useParams();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  function toggleRow(id) {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }

  useEffect(() => {
    let isMounted = true;

    async function loadPortfolio() {
      setLoading(true);
      setError("");

      const { data, error: rpcError } = await supabase.rpc(
        "get_public_convictions_by_username",
        { p_username: username }
      );

      if (!isMounted) return;

      if (rpcError) {
        setError(rpcError.message || "Failed to load portfolio.");
        setRows([]);
        setProfile(null);
        setLoading(false);
        return;
      }

      const convictions = data || [];
      setRows(convictions);

      if (convictions.length > 0) {
        setProfile({
          username: convictions[0].username,
          displayName: convictions[0].display_name || convictions[0].username,
          userId: convictions[0].user_id,
        });
      } else {
        const { data: publicProfile, error: profileError } = await supabase
          .from("profiles")
          .select("username, display_name")
          .eq("username", username)
          .eq("portfolio_public", true)
          .maybeSingle();

        if (profileError) {
          setError(profileError.message || "Failed to load portfolio.");
          setProfile(null);
        } else if (!publicProfile) {
          setError("Portfolio not found.");
          setProfile(null);
        } else {
          setProfile({
            username: publicProfile.username,
            displayName: publicProfile.display_name || publicProfile.username,
          });
        }
      }

      setLoading(false);
    }

    if (username) {
      loadPortfolio();
    }

    return () => {
      isMounted = false;
    };
  }, [username]);

  const convictionsWithComputed = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (rows || []).map((row) => {
      const startDateObj = row.start_date ? new Date(row.start_date) : null;
      const endDateObj = row.end_date ? new Date(row.end_date) : null;

      const hasStarted =
        startDateObj instanceof Date &&
        !Number.isNaN(startDateObj?.getTime?.()) &&
        startDateObj <= today;

      const closedByFlag = Boolean(row.is_closed);
      const autoClosed =
        endDateObj instanceof Date &&
        !Number.isNaN(endDateObj?.getTime?.()) &&
        endDateObj < today;

      const closed = closedByFlag || autoClosed;

      const effectiveEndPrice = closed
        ? (row.close_price ?? row.latest_price)
        : row.latest_price;

      const returnPct = computeReturnPct(row.start_price, effectiveEndPrice);
      const realReturn = getRealReturn(row.confidence, returnPct);

      return {
        ...row,
        hasStarted,
        closed,
        effectiveEndPrice,
        returnPct,
        realReturn,
      };
    });
  }, [rows]);

  const currentConvictions = useMemo(
    () => convictionsWithComputed.filter((c) => !c.closed),
    [convictionsWithComputed]
  );

  const completedConvictions = useMemo(
    () => convictionsWithComputed.filter((c) => c.closed),
    [convictionsWithComputed]
  );

  const totalClosedReturn = useMemo(() => {
    const valid = completedConvictions
      .map((row) => row.returnPct)
      .filter((v) => Number.isFinite(v));

    if (valid.length === 0) return null;
    return valid.reduce((sum, v) => sum + v, 0);
  }, [completedConvictions]);

  const totalClosedRealReturn = useMemo(() => {
    return completedConvictions.reduce((sum, row) => {
      return sum + (Number.isFinite(row.realReturn) ? row.realReturn : 0);
    }, 0);
  }, [completedConvictions]);

  const currentReturnIncludingOpen = useMemo(() => {
    const valid = convictionsWithComputed
      .map((row) => row.returnPct)
      .filter((v) => Number.isFinite(v));

    if (valid.length === 0) return null;
    return valid.reduce((sum, v) => sum + v, 0);
  }, [convictionsWithComputed]);

  const currentRealReturnIncludingOpen = useMemo(() => {
    return convictionsWithComputed.reduce((sum, row) => {
      return sum + (Number.isFinite(row.realReturn) ? row.realReturn : 0);
    }, 0);
  }, [convictionsWithComputed]);

  return (
    <div style={pageStyle}>
      <h1 style={{ marginBottom: 8 }}>
        {profile?.displayName || username}'s Portfolio
      </h1>
      <p style={{ color: "#666", marginTop: 0, marginBottom: 24 }}>
        majorah.com/portfolios/{username}
      </p>

      {loading ? <p>Loading portfolio...</p> : null}
      {!loading && error ? <p style={{ color: "#b91c1c" }}>{error}</p> : null}

      {!loading && !error ? (
        <>
          <div style={cardGridStyle}>
            <StatCard
              title="Total Return (Closed Convictions)"
              value={formatPercent(totalClosedReturn)}
            />
            <StatCard
              title="Total Real Return (Closed Convictions)"
              value={formatPercent(totalClosedRealReturn)}
            />
            <StatCard
              title="Current Return (Including Open Convictions)"
              value={formatPercent(currentReturnIncludingOpen)}
            />
            <StatCard
              title="Current Real Return (Including Open Convictions)"
              value={formatPercent(currentRealReturnIncludingOpen)}
            />
          </div>

          <div style={{ ...cardStyle, marginBottom: 28 }}>
            <h2 style={{ marginTop: 0 }}>Current Convictions</h2>

            {currentConvictions.length === 0 ? (
              <p>No current convictions.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: 44 }}></th>
                      <th style={thStyle}>Ticker</th>
                      <th style={thStyle}>Created</th>
                      <th style={thStyle}>Start Date</th>
                      <th style={thStyle}>End Date</th>
                      <th style={thStyle}>Confidence</th>
                      <th style={thStyle}>Timeline</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Start Price</th>
                      <th style={thStyle}>Latest Price</th>
                      <th style={thStyle}>Current Return</th>
                      <th style={thStyle}>Real Return</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentConvictions.map((row) => {
                      const pending = !row.hasStarted || !row.start_price;
                      const isExpanded = Boolean(expandedRows[row.id]);

                      return (
                        <React.Fragment key={row.id}>
                          <tr>
                            <td style={expandCellStyle}>
                              <button
                                type="button"
                                onClick={() => toggleRow(row.id)}
                                style={expandButtonStyle}
                                aria-label={isExpanded ? "Collapse rationale" : "Expand rationale"}
                                title={isExpanded ? "Hide rationale" : "Show rationale"}
                              >
                                {isExpanded ? "▾" : "▸"}
                              </button>
                            </td>
                            <td style={tdStyle}><strong>{row.ticker}</strong></td>
                            <td style={tdStyle}>{formatDateTime(row.created_at)}</td>
                            <td style={tdStyle}>{formatDate(row.start_date)}</td>
                            <td style={tdStyle}>{formatDate(row.end_date)}</td>
                            <td style={tdStyle}>{row.confidence}</td>
                            <td style={tdStyle}>{row.timeline}</td>
                            <td style={tdStyle}>
                              {pending ? (
                                <span style={{ color: "#a16207", fontWeight: 600 }}>Pending</span>
                              ) : (
                                <span style={{ color: "#15803d", fontWeight: 600 }}>Active</span>
                              )}
                            </td>
                            <td style={tdStyle}>
{row.start_price != null ? formatPrice(row.start_price) : "Pending"}
                            </td>
                            <td style={tdStyle}>
{row.latest_price != null ? formatPrice(row.latest_price) : "Pending"}
                            </td>
                            <td style={tdStyle}>{formatPercent(row.returnPct)}</td>
                            <td style={tdStyle}>{formatPercent(row.realReturn)}</td>
                          </tr>

                          {isExpanded ? (
                            <tr style={detailRowStyle}>
                              <td></td>
                              <td colSpan={11} style={detailContentStyle}>
                                <strong>Rationale:</strong>
                                <div style={{ marginTop: 8 }}> 
                                  {row.rationale || "—"}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h2 style={{ marginTop: 0 }}>Completed Convictions</h2>

            {completedConvictions.length === 0 ? (
              <p>No completed convictions.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: 44 }}></th>
                      <th style={thStyle}>Ticker</th>
                      <th style={thStyle}>Created</th>
                      <th style={thStyle}>Start Date</th>
                      <th style={thStyle}>End Date</th>
                      <th style={thStyle}>Confidence</th>
                      <th style={thStyle}>Timeline</th>
                      <th style={thStyle}>Start Price</th>
                      <th style={thStyle}>Close Price</th>
                      <th style={thStyle}>Total Return</th>
                      <th style={thStyle}>Real Return</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedConvictions.map((row) => {
                      const isExpanded = Boolean(expandedRows[row.id]);

                      return (
                        <React.Fragment key={row.id}>
                          <tr>
                            <td style={expandCellStyle}>
                              <button
                                type="button"
                                onClick={() => toggleRow(row.id)}
                                style={expandButtonStyle}
                                aria-label={isExpanded ? "Collapse rationale" : "Expand rationale"}
                                title={isExpanded ? "Hide rationale" : "Show rationale"}
                              >
                                {isExpanded ? "▾" : "▸"}
                              </button>
                            </td>
                            <td style={tdStyle}><strong>{row.ticker}</strong></td>
                            <td style={tdStyle}>{formatDateTime(row.created_at)}</td>
                            <td style={tdStyle}>{formatDate(row.start_date)}</td>
                            <td style={tdStyle}>{formatDate(row.end_date)}</td>
                            <td style={tdStyle}>{row.confidence}</td>
                            <td style={tdStyle}>{row.timeline}</td>
                            <td style={tdStyle}>
                              {row.start_price != null ? row.start_price : "—"}
                            </td>
                            <td style={tdStyle}>
                              {row.close_price != null ? row.close_price : "—"}
                            </td>
                            <td style={tdStyle}>{formatPercent(row.returnPct)}</td>
                            <td style={tdStyle}>{formatPercent(row.realReturn)}</td>
                          </tr>

                          {isExpanded ? (
                            <tr style={detailRowStyle}>
                              <td></td>
                              <td colSpan={10} style={detailContentStyle}>
                                <strong>Rationale:</strong>
                                <div style={{ marginTop: 8 }}>
                                  {row.rationale || "—"}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}